import { useState, useCallback, useMemo } from "react";
import { type OptionProps } from "@root/shared/components/SelectBox";
import { type BonusAvailableInputsProps, type ComponentHash } from "./type";

export const useBonusAvailableInputs = (
  componentHash: ComponentHash,
): BonusAvailableInputsProps => {
  const [bonusCheckboxChecked, setBonusCheckboxChecked] = useState(componentHash.checked);
  const [inputValues, setInputValues] = useState({
    bonusNoPaymentPastFlag: false,
    bonusPaymentYear: "",
    bonusPaymentCount: "",
    bonusPaymentTotalAmountMin: "",
    bonusPaymentTotalAmountMax: "",
    bonusPaymentConditions: "",
    ...componentHash.inputValues,
  });

  const parentCheckboxId = `work_requirement_welfares_${componentHash.value}`;

  // フォームの値をリセットする関数
  const clearFormValues = useCallback(() => {
    setInputValues({
      bonusNoPaymentPastFlag: false,
      bonusPaymentYear: "",
      bonusPaymentCount: "",
      bonusPaymentTotalAmountMin: "",
      bonusPaymentTotalAmountMax: "",
      bonusPaymentConditions: "",
    });
  }, []);

  // チェックボックスの変更をハンドルする関数
  const handleChangeBonusCheckbox = useCallback(
    (newValue: boolean) => {
      if (!newValue) {
        const hasInput =
          inputValues.bonusNoPaymentPastFlag ||
          inputValues.bonusPaymentYear !== "" ||
          inputValues.bonusPaymentCount !== "" ||
          inputValues.bonusPaymentTotalAmountMin !== "" ||
          inputValues.bonusPaymentTotalAmountMax !== "" ||
          inputValues.bonusPaymentConditions !== "";
        if (hasInput) {
          const userConfirmed = window.confirm(
            "チェックを外すと入力内容がリセットされます。本当にチェックを外しますか？",
          );
          if (!userConfirmed) {
            return;
          }
        }
        clearFormValues();
      }
      setBonusCheckboxChecked(newValue);
    },
    [clearFormValues, inputValues],
  );

  // 入力値の変更をハンドルする関数
  const handleInputChange = useCallback((field: string, value: string | boolean) => {
    setInputValues((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  }, []);

  const bonusAvailableInputsData = useMemo(() => {
    const { detailsSettings, formNames, name, value, errors } = componentHash;

    const bonusPaymentYearSelectOptions: OptionProps[] =
      detailsSettings.bonusPaymentYearSelectOptions.map((option) => ({
        label: option.toString(),
        value: option,
      }));

    const bonusPaymentCountSelectOptions: OptionProps[] =
      detailsSettings.bonusPaymentCountSelectOptions.map((option) => ({
        label: option.toString(),
        value: option,
      }));

    return {
      parentCheckboxId,
      text: name,
      bonusCheckboxChecked,
      handleChangeBonusCheckbox,
      handleInputChange,
      bonusPaymentYearSelectOptions,
      bonusPaymentCountSelectOptions,
      formNames,
      inputValues,
      errors,
      value,
    };
  }, [
    componentHash,
    bonusCheckboxChecked,
    inputValues,
    handleChangeBonusCheckbox,
    handleInputChange,
    parentCheckboxId,
  ]);

  return bonusAvailableInputsData;
};
